import { useEffect, useRef, useState } from "react";
import { RolesDataType } from "../../interfaces/admin.interface";
import { RolesService } from "../../services/priviligies.service";
import { Empty, message, Modal, Popconfirm } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import styles from "./Permissions.module.scss";
import { FaPlus } from "react-icons/fa";

const Permissions = () => {
  const [rolesVsPermissions, setRolesVsPermissions] = useState<RolesDataType[]>(
    []
  );
  const [permissions, setPermissions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const initialRolesValues = useRef<{ name: string; permissions: string[] }>({
    name: "",
    permissions: [],
  });

  useEffect(() => {
    RolesService.getAllPermissions().then((response) => {
      setPermissions(response?.data?.data);
    });
  }, []);
  useEffect(() => {
    setLoading(true);
    RolesService.getRolesWithPermissions()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: RolesDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setRolesVsPermissions(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const handleAfterSubmit = async () => {
    resetForm();
    setIsModalVisible(false);
    setEditModalVisible(false);
    setLoading(true);
    const updatedData = await RolesService.getRolesWithPermissions();
    setRolesVsPermissions(
      updatedData?.data?.data?.map((item: RolesDataType, index: number) => ({
        ...item,
        ...item.permissions,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      id: "",
      name: "",
      permissions: [] as string[],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Rolun adı tələb olunur"),
    }),
    onSubmit: async (values) => {
      const finalValues = {
        id: values.id,
        name: values.name,
        permissions: values.permissions,
      };
      if (editModalVisible) {
        const isEqual =
          values.name === initialRolesValues.current.name &&
          JSON.stringify([...values.permissions].sort()) ===
            JSON.stringify([...initialRolesValues.current.permissions].sort());
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          const response = await RolesService.updateRole(finalValues);
          if (response.status === 200) {
            message.success("Admin rolu uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Admin rolu yeniləmək alınmadı."
            );
          }
        }
      } else {
        const response = await RolesService.addRole(finalValues);
        if (response.status === 201) {
          message.success("Admin rolu uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(
            response.errorMessage || "Admin rolunu əlavə etmək alınmadı."
          );
        }
      }
      setLoading(false);
    },
  });

  const handleDelete = async (id: any) => {
    try {
      await RolesService.deleteRole(id);
      setRolesVsPermissions((prevData) =>
        prevData.filter((item) => item.id !== id)
      );
      message.success("Admin rol uğurla silindi!");
    } catch (error) {
      message.error("Admin rolu silmək alınmadı.");
    }
  };
  const handlePermissionChange = async (
    roleId: number,
    permission: string,
    isChecked: boolean
  ) => {
    const confirmMessage = isChecked
      ? `"${permission}" icazəsini əlavə etməyə əminsiniz?`
      : `"${permission}" icazəsini silməyə əminsiniz?`;

    Modal.confirm({
      centered: true,
      title: confirmMessage,
      okText: "Bəli",
      cancelText: "Xeyr",
      onOk: async () => {
        try {
          if (isChecked) {
            const response = await RolesService.addPermission(roleId, {
              permission,
            });
            if (response.status === 200) {
              message.success(`"${permission}" icazəsi uğurla əlavə edildi!`);
            } else {
              message.error(`"${permission}" icazəsini əlavə etmək alınmadı.`);
            }
          } else {
            const response = await RolesService.removePermission(roleId, {
              permission,
            });
            if (response.status === 200) {
              message.success(`"${permission}" icazəsi uğurla silindi!`);
            } else {
              message.error(`"${permission}" icazəsini silmək alınmadı.`);
            }
          }
          const updatedData = await RolesService.getRolesWithPermissions();
          setRolesVsPermissions(
            updatedData?.data?.data.map(
              (item: RolesDataType, index: number) => ({
                ...item,
                key: index + 1,
              })
            )
          );
        } catch (error) {
          message.error("Xəta baş verdi.");
        }
      },
      onCancel: () => {
        message.info("Əməliyyat ləğv edildi.");
      },
    });
  };

  const handleEdit = (role: RolesDataType) => {
    setEditModalVisible(true);
    setIsModalVisible(true);
    setFieldValue("id", role.id);
    setFieldValue("name", role.name);
    setFieldValue(
      "permissions",
      role.permissions.map((perm) => perm.name)
    );
    initialRolesValues.current = {
      name: role.name,
      permissions: role.permissions.map((per) => per.name) as string[],
    };
  };

  return (
    <div id="pages">
      <div className="page_head">
        <h3 className="page_title">Rollar & İcazələr</h3>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <div className="pages_components">
          <div className="table_head">
            <MainButton
              onClick={() => {
                setIsModalVisible(true);
                resetForm();
              }}
            >
              <FaPlus size={20} />
            </MainButton>
          </div>
          <div>
            {rolesVsPermissions.length > 0 ? (
              <table className={styles.permissions_table}>
                <thead>
                  <tr>
                    <th></th>
                    {permissions?.map((element, idx) => (
                      <th key={idx} className={styles.permissions_name}>
                        {element}
                      </th>
                    ))}
                    <th>
                      <div className="settings">
                        <i className="fa-solid fa-gear"></i>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rolesVsPermissions.map((role, roleIdx) => (
                    <tr key={roleIdx}>
                      <td>{role.name}</td>
                      {permissions.map((perm, permIdx) => {
                        const isChecked = role.permissions?.some(
                          (rPerm) => rPerm.name === perm
                        );
                        return (
                          <td key={permIdx} className={styles.checkbox_wrapper}>
                            <input
                              type="checkbox"
                              id={`checkbox-${roleIdx}-${permIdx}`}
                              name={`${role}-${permIdx}`}
                              checked={isChecked}
                              onChange={(e) =>
                                handlePermissionChange(
                                  role.id,
                                  perm,
                                  e.target.checked
                                )
                              }
                            />
                          </td>
                        );
                      })}
                      <td>
                        <div className="settings_icons">
                          <div
                            className="edit"
                            onClick={() => handleEdit(role)}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </div>
                          <Popconfirm
                            title="Bu admin rolunu siləcəyinizə əminsiniz?"
                            onConfirm={() => handleDelete(role.id)}
                            onCancel={() =>
                              message.info("Silinmə əməliyyatı ləğv edildi.")
                            }
                            okText="Bəli"
                            cancelText="Xeyr"
                            placement="topRight"
                          >
                            <div className="delete">
                              <i className="fa-solid fa-trash"></i>
                            </div>
                          </Popconfirm>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.no_data_icon}>
                <Empty description="No data" />
              </div>
            )}
          </div>
        </div>
      )}
      <Modal
        title={editModalVisible ? "İcazəi redaktə edin" : "İcazə əlavə edin"}
        open={isModalVisible || editModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditModalVisible(false);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="name">
              Adminin rolu
            </label>
            <div className="form_input">
              <div className="input_wrapper">
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={values.name}
                  placeholder="Adminin rolu"
                  onChange={handleChange}
                />
                <span className="focus_border"></span>
              </div>
              <ErrorMessage error={errors.name} touched={touched.name} />
            </div>
          </div>
          <div>
            <div className="form_input">
              <h4 className="roles_title">İcazələr</h4>
              <div className="checkbox_wrapper">
                {permissions?.map((name: string, idx: number) => (
                  <div key={idx} className="checkbox">
                    <input
                      type="checkbox"
                      value={name}
                      id={`permission-${idx}`}
                      name="permissions"
                      checked={values.permissions.includes(name)}
                      onChange={(e) => {
                        const updatedPermissions = e.target.checked
                          ? [...values.permissions, name]
                          : values.permissions.filter((perm) => perm !== name);
                        setFieldValue("permissions", updatedPermissions);
                      }}
                    />
                    <label htmlFor={`permission-${idx}`}>{name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {editModalVisible ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Permissions;
